/**
 * Dark Mode Toggle Component
 * Simple button toggle matching admin panel style
 */

import { useEffect, useState } from "react";
import { FiSun, FiMoon } from "react-icons/fi";
import "../styles/DarkModeToggle.css";

// All available theme class names
const ALL_THEMES = ['christmas', 'halloween', 'ocean', 'sunset', 'forest', 'lavender', 'rosegold', 'midnight', 'autumn', 'spring', 'monochrome'];

const DarkModeToggle = ({ disableSeasonalThemes = false }) => {
  const [isDark, setIsDark] = useState(false);
  const [activeTheme, setActiveTheme] = useState('none');

  // Check for saved theme preference or default to light mode
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');

    if (!savedTheme) {
      localStorage.setItem('theme', 'light');
      setIsDark(false);
      document.documentElement.classList.remove('dark');
    } else if (savedTheme === 'dark') {
      setIsDark(true);
      document.documentElement.classList.add('dark');
    } else {
      setIsDark(false);
      document.documentElement.classList.remove('dark');
    }

    // Check for active theme
    const storedActiveTheme = localStorage.getItem('activeTheme');
    if (storedActiveTheme && storedActiveTheme !== 'none') {
      setActiveTheme(storedActiveTheme);
      if (!disableSeasonalThemes) {
        document.documentElement.classList.add(storedActiveTheme);
      }
    } else {
      // Fallback to legacy keys
      const christmasMode = localStorage.getItem('christmasMode') === 'true';
      const halloweenMode = localStorage.getItem('halloweenMode') === 'true';
      if (christmasMode) {
        setActiveTheme('christmas');
        if (!disableSeasonalThemes) {
          document.documentElement.classList.add('christmas');
        }
      } else if (halloweenMode) {
        setActiveTheme('halloween');
        if (!disableSeasonalThemes) {
          document.documentElement.classList.add('halloween');
        }
      }
    }
  }, [disableSeasonalThemes]);

  // Listen for theme changes
  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          const classes = document.documentElement.classList;
          const currentTheme = ALL_THEMES.find(theme => classes.contains(theme)) || 'none';
          setActiveTheme(currentTheme);
        }
      });
    });

    observer.observe(document.documentElement, { attributes: true });

    const handleStorageChange = (e) => {
      if (e.key === 'activeTheme') {
        setActiveTheme(e.newValue || 'none');
      }
      if (e.key === 'christmasMode' && e.newValue === 'true') {
        setActiveTheme('christmas');
      }
      if (e.key === 'halloweenMode' && e.newValue === 'true') {
        setActiveTheme('halloween');
      }
    };
    window.addEventListener('storage', handleStorageChange);

    return () => {
      observer.disconnect();
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const toggleDarkMode = () => {
    const newTheme = !isDark;
    setIsDark(newTheme);

    if (newTheme) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  };

  // Get theme-specific class
  const getThemeClass = () => {
    if (activeTheme === 'christmas') return 'theme-christmas';
    if (activeTheme === 'halloween') return 'theme-halloween';
    return '';
  };

  return (
    <button
      className={`theme-toggle-btn ${getThemeClass()}`}
      onClick={toggleDarkMode}
      title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      aria-label={`Switch to ${isDark ? 'light' : 'dark'} mode`}
    >
      {activeTheme === 'christmas' ? (
        // Christmas tree icon
        <svg viewBox="0 0 24 24" fill="none" className="theme-icon">
          <path d="M12 2L8 8h2l-3 6h2l-4 8h14l-4-8h2l-3-6h2L12 2z" fill="currentColor"/>
          <rect x="10" y="20" width="4" height="2" fill="#8B4513"/>
        </svg>
      ) : isDark ? (
        <FiSun className="theme-icon" />
      ) : (
        <FiMoon className="theme-icon" />
      )}
    </button>
  );
};

export default DarkModeToggle;
